import { observable, action, extendObservable } from "mobx";
import Api from "../service/Api";
import Customer from "../models/Customer";

// add a title property to customer so it's compliant with Semantic UI's Search widget.
const getCustomerTitle = customer => {
  let title = `${customer.lastName}, ${customer.firstName}`;

  if (customer.company) {
    title = `${title} (${customer.company})`;
  }

  return title;
};

export default class CustomerMerge {
  // bool. If searching for customers or not.
  @observable searching = false;

  // string
  @observable searchValue = "";

  // array. Search results.
  @observable searchResults = [];

  // bool/ For xhr other than searching for customers.
  @observable loading = false;

  // bool
  @observable modalOpen = false;

  // Customer
  @observable customerFrom;

  // Customer
  @observable customerTo;

  constructor() {
    this.customerFrom = new Customer({});
    this.customerTo = new Customer({});
  }

  @action
  reset() {
    this.searching = false;
    this.searchValue = "";
    this.searchResults.clear();
    this.loading = false;
    this.modalOpen = false;
    this.customerFrom = new Customer({});
    this.customerTo = new Customer({});
  }

  @action
  setCustomerFromData(data) {
    this.customerFrom = new Customer(data);
    extendObservable(this.customerFrom, {
      toursCount: data.toursCount,
    });
  }

  @action
  openModal(customerId) {
    this.modalOpen = true;

    this.fetchCustomerInfo(customerId).then(data => {
      this.setCustomerFromData(data.customer);
    });
  }

  @action
  closeModal() {
    // prevent closing if we are in the middle of a xhr save.
    if (this.loading) {
      return;
    }

    this.modalOpen = false;
  }

  @action
  handleSearchChange(searchValue) {
    this.searchValue = searchValue;
    this.customerTo = new Customer({});

    // Do not attempt a request if there's no search criteria (like when deleting all chars with delete keyboard button)
    if (this.searchValue.trim() === "") {
      this.searching = false;
      this.searchResults.clear();

      return;
    }

    this.searching = true;

    Api.get("/merge-tool/customers", {
      query: encodeURIComponent(this.searchValue),
    })
      .then(data => {
        this.searching = false;
        // only set them if there's a search value. This prevents a race condition where a list of customers
        // is shown right after deleting the last char from the input that holds the searchValue.
        if (this.searchValue !== "") {
          this.setSearchResults(data.customers);
        }
      })
      .catch(err => {
        alert("An unexpected error occurred. Please try later.");
        this.searching = false;

        throw err;
      });
  }

  @action
  setSearchResults(customers) {
    this.searchResults.clear();

    for (let i = 0; i < customers.length; i += 1) {
      const customer = customers[i];
      // Prevent showing the customerFrom in the results, because it doesn't make sense to
      // merge a customer into itself.
      if (customer.customerId !== this.customerFrom.customerId) {
        // No need to be a full Customer class object.
        this.searchResults.push(customer);
      }
    }
  }

  @action
  handleSearchSelect(selectedCustomerTitle) {
    const selectedCustomerData = this.searchResults.find(
      customer => selectedCustomerTitle === getCustomerTitle(customer),
    );

    if (!selectedCustomerData) {
      throw new Error(`Could not find selected customer with title: ${selectedCustomerTitle}`);
    }

    this.fetchCustomerInfo(selectedCustomerData.customerId).then(data => {
      this.customerTo = new Customer(data.customer);
    });
  }

  @action
  goToStep1() {
    this.customerTo = new Customer({});
    this.searchValue = "";
    this.searchResults.clear();
  }

  @action
  merge() {
    if (!window.confirm("This action is not reversible. Do you want to continue?")) {
      return Promise.resolve();
    }

    this.loading = true;

    return Api.post("/merge-tool/customers/merge", {
      customerFrom: this.customerFrom.customerId,
      customerTo: this.customerTo.customerId,
    })
      .then(() => {
        alert("Success!");
        window.location.reload();
      })
      .catch(err => {
        alert("An unexpected error occurred. Please try later.");
        this.searching = false;

        throw err;
      });
  }

  fetchCustomerInfo(customerId) {
    this.loading = true;

    return Api.get(`/merge-tool/customers/${customerId}`)
      .catch(err => {
        alert("An unexpected error occurred. Please try later.");
        this.loading = false;

        throw err;
      })
      .finally(() => {
        this.loading = false;
      });
  }
}

export { getCustomerTitle };
